import { useCallback, useEffect, useState } from 'react';
import { PackagePlus, Truck } from '../../lib/icons';
import { toast } from 'sonner';
import {
  fetchFormats,
  fetchContactsRoles,
  fetchApproEmballages,
  enregistrerApproEmballages,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { ContactCombobox } from '../shared/ContactCombobox';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

export const ApproEmballages = ({ onDone }) => {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [formats, setFormats] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [bons, setBons] = useState([]);
  const [f, setF] = useState({ date: aujourdhui, tiersId: '', piece: '', notes: '' });
  const [lignes, setLignes] = useState({});

  const load = useCallback(async () => {
    setFormats(await fetchFormats());
    setTiers((await fetchContactsRoles()).filter((c) => c.type !== 'personnel'));
    setBons(await fetchApproEmballages());
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const maj = (id, patch) => setLignes((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  const detail = Object.entries(lignes)
    .map(([formatId, l]) => ({ formatId, unites: Number(l.unites) || 0, prixUnitaire: Number(l.prixUnitaire) || 0 }))
    .filter((l) => l.unites > 0);
  const total = detail.reduce((s, l) => s + l.unites * l.prixUnitaire, 0);

  const submit = async (e) => {
    e.preventDefault();
    const fournisseur = tiers.find((t) => t.id === f.tiersId)?.nom;
    const { error, row } = await enregistrerApproEmballages({ ...f, fournisseur, lignes: detail });
    if (error) return toast.error(error);
    toast.success(`Bon ${row.id} — ${fmt(row.unites)} emballages entrés en stock`);
    setLignes({});
    setF({ ...f, piece: '', notes: '' });
    load();
    onDone?.();
  };

  return (
    <div data-testid="appro-emballages" className="space-y-6">
      <form onSubmit={submit} data-testid="form-appro" className="border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
            <Truck size={16} /> Bon de réception fournisseur — emballages vides
          </h2>
          <p className="text-xs text-slate-500">
            Achat de consommables : bouteilles et bidons vides. Le stock physique augmente et le montant
            alimente le compte du fournisseur.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className={label}>Date de réception</label>
            <input data-testid="appro-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} />
          </div>
          <div>
            <label className={label}>Fournisseur</label>
            <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF({ ...f, tiersId: id })} testid="appro-fournisseur" />
          </div>
          <div>
            <label className={label}>N° bon / facture fournisseur</label>
            <input data-testid="appro-piece" value={f.piece} onChange={(e) => setF({ ...f, piece: e.target.value })} className={field} />
          </div>
          <div>
            <label className={label}>Notes</label>
            <input data-testid="appro-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={field} />
          </div>
        </div>

        <div className="px-4 pb-4">
          <p className={label}>Quantités reçues</p>
          <div className="mt-2 divide-y divide-slate-100 border border-slate-200">
            {formats.map((x) => (
              <div key={x.id} data-testid={`appro-ligne-${x.id}`} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <div className="min-w-[180px] flex-1">
                  <p className="text-sm font-semibold text-slate-900">{x.nom}</p>
                  <p className="font-data text-[11px] text-slate-500">
                    {x.litres} L · en stock : {fmt(x.stock)} vides
                  </p>
                </div>
                <input
                  data-testid={`appro-unites-${x.id}`}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Unités"
                  value={lignes[x.id]?.unites ?? ''}
                  onChange={(e) => maj(x.id, { unites: e.target.value })}
                  className="h-11 w-28 border border-slate-300 bg-slate-50 px-2 text-center font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
                <input
                  data-testid={`appro-prix-${x.id}`}
                  type="number"
                  min="0"
                  step="0.001"
                  placeholder={`Prix u. (${fmt(x.prixEmballage, 3)})`}
                  value={lignes[x.id]?.prixUnitaire ?? ''}
                  onChange={(e) => maj(x.id, { prixUnitaire: e.target.value })}
                  className="h-11 w-36 border border-slate-300 bg-slate-50 px-2 text-center font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 px-4 py-4">
          <button type="submit" data-testid="appro-submit" className="flex h-11 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700">
            <PackagePlus size={16} /> Valider le bon de réception
          </button>
          <p data-testid="appro-total" className="font-data text-sm text-slate-700">
            {fmt(detail.reduce((s, l) => s + l.unites, 0))} unité(s) · {fmt(total, 3)} TND
          </p>
        </div>
      </form>

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
          Bons de réception d'emballages
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-appro">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Réf.', 'Date', 'Fournisseur', 'N° pièce', 'Détail', 'Unités', 'Total (TND)'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bons.length === 0 ? (
                <tr data-testid="appro-empty">
                  <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500">
                    Aucun bon enregistré — le stock d'emballages vides n'a pas encore été approvisionné.
                  </td>
                </tr>
              ) : (
                bons.map((b) => (
                  <tr key={b.id} data-testid={`appro-row-${b.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{b.id}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{b.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">{b.fournisseur}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{b.piece || '—'}</td>
                    <td className="px-3 py-2 font-data text-[11px] text-slate-600">
                      {b.lignes.map((l) => `${l.unites} × ${l.nom}`).join(' · ')}
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(b.unites)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(b.total, 3)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
